import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useAppointment from '../../../../../../../hooks/useAppointment';
import { useBooth } from '../../../../../../../hooks/useBooth';
import { useMobileApp } from '../../../../../../../hooks/useMobileApp';
import { useWindowSize } from '../../../../../../../hooks/useWindowSize';
import { Booth, Meetingroom } from '../../../../../../../interfaces/interfaces';
import { UiComponent } from '../../../../../../../interfaces/pagebuilder';
import { setTeamUsers } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../../../reduxToolkit/typedRedux';
import { getAllUsers } from '../../../../../../../services/services';
import { AppointmentCreateModal } from '../../../../../../account/Appointment/AppointmentCreateModal';
import BoothHeaderImages from '../../../../../../account/Booth/BoothInfo/BoothHeaderImages/BoothHeaderImages';
import BoothPDFs from '../../../../../../account/Booth/BoothInfo/BoothPDFs';
import BoothSlogan from '../../../../../../account/Booth/BoothInfo/BoothSlogan';
import BoothVideoAndRichtext from '../../../../../../account/Booth/BoothInfo/BoothVideoAndRichtext';
import BoothPresentations from '../../../../../../account/Booth/BoothPresentations/BoothPresentations';
import VyButton from '../../../../../../common/VyButton';
import VyTooltip from '../../../../../../common/VyTooltip';
import AppointmentBoard from '../../../appointment/AppointmentBoard';
import NetworkRoom from '../../../network/NetworkRoom';
import Section from '../../BigBoothTwoColsSection';
import BoothLinkedPersonEdit from '../../BoothLinkedPersonEdit';
import Breadcrumb from '../../Breadcrumb';
import ChildBooth from '../../ChildBooth';
import MeetingRoomTemplateEdit from '../../MeetingRoomTemplateEdit';
import NewBoothLinkedPerson from '../../NewBoothLinkedPerson';
import SmallBooth from '../../SmallBooth';
import AddRoomButton from '../AddRoomButton';
import HeaderEdit from '../HeaderEdit';

interface BigBoothEditInterface {
  booth: Booth;
}

export interface Section {
  title: string;
  meetingrooms: Meetingroom[] | undefined;
  type: 'meeting' | 'conference';
  limit: number;
  onAdd: () => Promise<void>;
}

const BigBoothEdit = ({ booth }: BigBoothEditInterface) => {
  const event = useSelector((state) => state.common.event);
  const networks = useSelector((state) => state.common.networks);
  const orgMembers = useSelector(({ common }) => common.teamUsers);
  const user = useSelector(({ common }) => common.user);
  const pageData = useSelector((state) => state.common.pagedata);

  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);
  const [uiComponent, setUiComponent] = useState<UiComponent>();
  const [isSpotlight, setIsSpotlight] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const { t } = useTranslation(['booth']);
  const { updateProp, createNewMeetingroom, createNewConferenceRoom, fetchAndUpdateBooth, deleteLinkedPerson } = useBooth();
  const dispatch = useDispatch();
  const { initializeAppointments } = useAppointment(booth._id);
  const router = useRouter();
  const { width } = useWindowSize();
  const { isAppView } = useMobileApp();

  const fetchMembers = async () => {
    if (orgMembers.length > 0) return;
    try {
      const { data } = await getAllUsers(50);
      dispatch(setTeamUsers([{ ...user!, organization: user!.organization!._id }, ...data.filter((m) => m._id !== user!._id)]));
    } catch (error) {
      console.error(error);
    }
  };

  const getUiComponent = () => {
    const component = pageData?.uicomponents.find((component) => component._id === router.query.componentId);

    if (component) {
      setUiComponent(component);
      setIsSpotlight(!!component.spotlightBooths?.find((booth) => booth === router.query.openId));
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    getUiComponent();
  }, [router.query.componentId]);

  useEffect(() => {
    if (!event) return;
    const meetingTypeMeetingrooms = booth?.meetingrooms ?? [];
    const meetingTypeConferencerooms = booth?.conferencerooms ?? [];
    const _sections: Section[] = [
      {
        title: event?.template.individualNames.meetingrooms || t('booth:chatrooms'),
        meetingrooms: meetingTypeMeetingrooms,
        type: 'meeting',
        limit: booth.maxMeetings,
        onAdd: async () => await createNewMeetingroom(booth._id, () => fetchAndUpdateBooth(booth._id)),
      },
      {
        title: event?.template.individualNames.conferencerooms || t('booth:conferencerooms'),
        meetingrooms: meetingTypeConferencerooms,
        type: 'conference',
        limit: booth.maxConferences,
        onAdd: async () => await createNewConferenceRoom(booth._id, () => fetchAndUpdateBooth(booth._id)),
      },
    ];
    setSections(_sections);
  }, [event, booth]);

  if (!booth) return <></>;

  const boothNetworks = networks.filter((network) => network?.booth?._id === booth._id);

  return (
    <div className="bg-white">
      <div className="min-h-fit">
        <BoothHeaderImages booth={booth} />
      </div>
      <div className="px-8 py-2 bg-white">
        <div className="flex flex-col gap-8 w-full md:items-center md:flex-row md:max-h-52">
          <div>
            <HeaderEdit booth={booth} update={async (path, value) => await updateProp(booth._id, path, value)} showPreview={showPreview} />
          </div>
          {!showPreview ? (
            <VyButton compact onClick={() => setShowPreview(true)} endIcon={<FontAwesomeIcon icon={faEye} />}>
              {t('booth:preview')}
            </VyButton>
          ) : (
            <div className={`${isSpotlight && (isAppView() || width! > 1200) ? 'w-2/3' : 'w-64'} scale-75 shrink-0 h-fit relative`}>
              <VyTooltip title={t('booth:hide-preview')} placement="top">
                <FontAwesomeIcon
                  icon={faEye}
                  size="2x"
                  className="absolute -top-3 -left-4 z-50 p-2 rounded-full transition-colors cursor-pointer bg-neutral-200 hover:text-red-500"
                  onClick={() => setShowPreview(false)}
                />
              </VyTooltip>
              <SmallBooth booth={booth} event={event!} spotlight={isSpotlight} uiComponent={uiComponent} previewOnly />
            </div>
          )}
        </div>
      </div>
      <BoothSlogan booth={booth} />

      <Breadcrumb booth={booth} className="px-12 pt-4 pb-3" />
      <main className="flex flex-col p-8 pb-8">
        <BoothVideoAndRichtext booth={booth} onRefresh={() => fetchAndUpdateBooth(booth._id)} isInline />
        {booth.childBooths && booth.childBooths.length > 0 && (
          <Section title={event?.template.individualNames.subBooths || ''}>
            {booth.childBooths.map((booth) => (
              <ChildBooth key={booth._id} booth={booth} />
            ))}
          </Section>
        )}
        <div className="my-4">
          <BoothPDFs booth={booth} isInline onRefresh={() => fetchAndUpdateBooth(booth._id)} />
        </div>

        {event?.settings?.isLinkedBoothMembersActive && (
          <Section
            twoCols={booth.linkedPeople && booth.linkedPeople.length > 1}
            title={event?.template.individualNames.linkedPeople || t('booth:linked-people')}>
            {booth.linkedPeople &&
              booth.linkedPeople
                .map((person) => ({ ...person, avatar: person.avatar ?? person.user.avatar, name: person.name ?? person.user.name }))
                .map((person) => (
                  <BoothLinkedPersonEdit
                    key={person.user._id}
                    person={person}
                    booth={booth}
                    onLinkedPersonDelete={() => deleteLinkedPerson(booth, person.user._id, true)}
                  />
                ))}
            {booth.linkedPeople && booth.linkedPeople.length < orgMembers.length && <NewBoothLinkedPerson booth={booth} />}
          </Section>
        )}
        {sections.map((section, i) => {
          return (
            <div
              key={section.title}
              className="rounded-sm ring-offset-4 outline-none focus:ring-2 ring-vystemblue"
              id={`${section.type}rooms-focus`}
              tabIndex={0}>
              {section && (
                <>
                  <Section twoCols={false} title={section.title}>
                    {section?.meetingrooms?.map((meetingroom) => (
                      <MeetingRoomTemplateEdit key={meetingroom._id} meetingroom={meetingroom} booth={booth} hideJoinButton />
                    ))}
                  </Section>
                  <AddRoomButton
                    booth={booth}
                    onAdd={section.onAdd}
                    type={section.type}
                    limit={section.limit}
                    sectionValue={section.meetingrooms?.length ?? 0}
                  />
                </>
              )}
            </div>
          );
        })}
        <div
          key={event?.template.individualNames.presentationrooms || t('account:presentations')}
          className="max-w-full rounded-sm ring-offset-4 outline-none focus:ring-2 ring-vystemblue"
          id="presentationrooms-focus"
          tabIndex={0}>
          <Section twoCols={false} title={event?.template.individualNames.presentationrooms || t('account:presentations')}>
            <BoothPresentations booth={booth} isInline onRefresh={() => fetchAndUpdateBooth(booth._id)} />
          </Section>
        </div>
        {event?.modules?.booking?.active && booth?.settings?.enableAppointments.value && (
          <>
            <h1 className="mt-4 mb-4 text-xl font-bold">{event.template.individualNames.appointments}</h1>
            {booth && booth?.meetingrooms?.length > 0 && (
              <VyButton className="mb-4" onClick={() => setShowAddAppointment(true)}>
                {t('appointment:add-appointment-slot')}
              </VyButton>
            )}
            <div className="overflow-auto pt-2 pb-4 w-full">
              <AppointmentBoard
                event={event}
                boothId={booth._id}
                date={event.datetime_start}
                endDate={event.datetime_end}
                meetingrooms={booth?.meetingrooms}
                refresh={initializeAppointments}
              />
            </div>
            {showAddAppointment && booth && booth?.meetingrooms && (
              <AppointmentCreateModal
                booth={booth}
                onCreate={() => {
                  initializeAppointments(true);
                  setShowAddAppointment(false);
                }}
                open={showAddAppointment}
                onClose={() => setShowAddAppointment(false)}
              />
            )}
          </>
        )}
        {booth?.settings?.displayNetworkingroomsInBooth?.value && boothNetworks.length > 0 && (
          <>
            <h1 className="mt-4 mb-4 text-xl font-bold">{t('booth:networking-rooms')}</h1>
            {boothNetworks.map((network) => (
              <div className="flex overflow-auto justify-center pt-2 pb-4 w-full">
                <NetworkRoom key={network._id} network={network} />
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
};
export default BigBoothEdit;
